import { useEffect, useState, useCallback } from "react";
import { Loader2, Eye, Trash2, X } from "lucide-react";
import { adminApi } from "../../lib/auth";
import FilterBar from "../../components/admin/FilterBar";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "qualified", "converted", "lost"];

const statusColor = (s) => {
  switch (s) {
    case "new":
      return "bg-[#1d4ed8]/15 text-[#60a5fa] border-[#1d4ed8]/30";
    case "contacted":
      return "bg-amber-500/15 text-amber-300 border-amber-500/30";
    case "qualified":
      return "bg-purple-500/15 text-purple-300 border-purple-500/30";
    case "converted":
      return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
    case "lost":
      return "bg-[#dc2626]/15 text-[#f87171] border-[#dc2626]/30";
    default:
      return "bg-white/5 text-neutral-300 border-white/10";
  }
};

export default function AdminLeads() {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status && status !== "all") params.status = status;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await adminApi.get("/admin/leads", { params });
      setItems(data.items);
      setCount(data.count);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [q, status, dateFrom, dateTo]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const updateLead = async (id, payload) => {
    try {
      const { data } = await adminApi.patch(`/admin/leads/${id}`, payload);
      setItems((rows) => rows.map((r) => (r.id === id ? data : r)));
      if (selected?.id === id) setSelected(data);
      toast.success("Lead updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const deleteLead = async (id) => {
    if (!confirm("Delete this lead permanently?")) return;
    try {
      await adminApi.delete(`/admin/leads/${id}`);
      setItems((rows) => rows.filter((r) => r.id !== id));
      setSelected(null);
      toast.success("Lead deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status && status !== "all") params.set("status", status);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    const token = localStorage.getItem("dvp_admin_token");
    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/export?${params.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!res.ok) {
      toast.error("Export failed");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8 space-y-6" data-testid="admin-leads-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-3xl text-white tracking-tight">
            Leads / CRM
          </h1>
          <p className="text-neutral-400 text-sm mt-1">
            Manage sales pipeline. Filter, update status, take notes and export.
          </p>
        </div>
      </div>

      <FilterBar
        q={q}
        setQ={setQ}
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        status={status}
        setStatus={setStatus}
        statuses={STATUSES}
        count={count}
        onRefresh={load}
        onExport={exportCsv}
      />

      <div className="rounded-xl border border-white/10 bg-[#0f0f0f] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0a0a0a] border-b border-white/10">
              <tr className="text-left text-[11px] uppercase font-mono tracking-[0.2em] text-neutral-500">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Mobile</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">City</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-12">
                    <Loader2 className="h-5 w-5 animate-spin inline text-[#dc2626]" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-neutral-500">
                    No leads found
                  </td>
                </tr>
              ) : (
                items.map((l) => (
                  <tr
                    key={l.id}
                    data-testid={`lead-row-${l.id}`}
                    className="border-b border-white/5 hover:bg-white/[0.02]"
                  >
                    <td className="px-4 py-3 text-neutral-400 font-mono text-xs">
                      {new Date(l.created_at).toLocaleDateString()}
                      <br />
                      <span className="text-neutral-600">
                        {new Date(l.created_at).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white font-medium">
                      {l.full_name}
                      {l.company_name ? (
                        <div className="text-xs text-neutral-500">{l.company_name}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 font-mono text-neutral-200">
                      <a href={`tel:${l.mobile}`} className="hover:text-white">
                        {l.mobile}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-neutral-200">
                      <a href={`mailto:${l.email}`} className="hover:text-white truncate">
                        {l.email}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-neutral-300">{l.product_required}</td>
                    <td className="px-4 py-3 text-neutral-400">{l.city || "—"}</td>
                    <td className="px-4 py-3">
                      <select
                        value={l.status}
                        onChange={(e) => updateLead(l.id, { status: e.target.value })}
                        data-testid={`lead-status-${l.id}`}
                        className={`rounded-full border text-[11px] px-3 py-1 font-mono uppercase tracking-wider focus:outline-none ${statusColor(
                          l.status
                        )}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s} className="bg-[#0a0a0a] text-white">
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setSelected(l)}
                        data-testid={`lead-view-${l.id}`}
                        className="inline-flex items-center gap-1 text-neutral-300 hover:text-white px-2 py-1 rounded hover:bg-white/5"
                      >
                        <Eye className="h-3.5 w-3.5" /> View
                      </button>
                      <button
                        onClick={() => deleteLead(l.id)}
                        data-testid={`lead-delete-${l.id}`}
                        className="inline-flex items-center gap-1 text-[#dc2626] hover:bg-[#dc2626]/10 px-2 py-1 rounded ml-1"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <LeadDrawer
          lead={selected}
          onClose={() => setSelected(null)}
          onUpdate={(payload) => updateLead(selected.id, payload)}
        />
      )}
    </div>
  );
}

function LeadDrawer({ lead, onClose, onUpdate }) {
  const [notes, setNotes] = useState(lead.notes || "");
  const [assigned, setAssigned] = useState(lead.assigned_to || "");

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-[#0f0f0f] border-l border-white/10 h-full overflow-auto"
      >
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#dc2626]">
              Lead Detail
            </div>
            <h2 className="font-display font-bold text-2xl text-white mt-1">
              {lead.full_name}
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="lead-drawer-close"
            className="text-neutral-400 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <KV label="Mobile" value={lead.mobile} link={`tel:${lead.mobile}`} />
            <KV label="Email" value={lead.email} link={`mailto:${lead.email}`} />
            <KV label="Customer Type" value={lead.customer_type} />
            <KV label="Product" value={lead.product_required} />
            <KV label="City" value={lead.city || "—"} />
            <KV label="Company" value={lead.company_name || "—"} />
          </div>

          {/* LOAN fields */}
          {(lead.loan_amount || lead.existing_banking_relationship || lead.purpose) && (
            <div className="border-t border-white/10 pt-5">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#dc2626] mb-3">
                Loan Details
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <KV label="Loan Amount" value={lead.loan_amount || "—"} />
                <KV
                  label="Existing Bank"
                  value={lead.existing_banking_relationship || "—"}
                />
                {lead.purpose && (
                  <div className="col-span-2">
                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-1">
                      Purpose
                    </div>
                    <div className="text-white">{lead.purpose}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* INSURANCE fields */}
          {(lead.insurance_type || lead.sum_assured || lead.family_members || lead.existing_policy) && (
            <div className="border-t border-white/10 pt-5">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#1d4ed8] mb-3">
                Insurance Details
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <KV label="Insurance Type" value={lead.insurance_type || "—"} />
                <KV label="Sum Assured" value={lead.sum_assured || "—"} />
                <KV label="Age" value={lead.age || "—"} />
                <KV label="Family Members" value={lead.family_members || "—"} />
                <KV label="Existing Policy" value={lead.existing_policy || "—"} />
              </div>
            </div>
          )}

          {/* INVESTMENT fields */}
          {(lead.investment_type || lead.monthly_investment || lead.lumpsum_amount || lead.investment_horizon || lead.risk_appetite || lead.investment_goal) && (
            <div className="border-t border-white/10 pt-5">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#1d4ed8] mb-3">
                Investment Details
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <KV label="Investment Type" value={lead.investment_type || "—"} />
                <KV label="Goal" value={lead.investment_goal || "—"} />
                <KV label="Monthly SIP" value={lead.monthly_investment || "—"} />
                <KV label="Lumpsum" value={lead.lumpsum_amount || "—"} />
                <KV label="Horizon" value={lead.investment_horizon || "—"} />
                <KV label="Risk Appetite" value={lead.risk_appetite || "—"} />
              </div>
            </div>
          )}

          {lead.message && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-1.5">
                Message
              </div>
              <div className="text-neutral-200 text-sm bg-[#1a1a1a] border border-white/10 rounded p-3 whitespace-pre-wrap">
                {lead.message}
              </div>
            </div>
          )}

          <div className="border-t border-white/10 pt-5 space-y-4">
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#1d4ed8]">
              CRM
            </div>

            <div>
              <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-1.5">
                Assigned to
              </label>
              <input
                value={assigned}
                onChange={(e) => setAssigned(e.target.value)}
                placeholder="Sales rep name"
                data-testid="lead-assigned-input"
                className="w-full bg-[#1a1a1a] border border-white/10 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#dc2626]"
              />
            </div>

            <div>
              <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-1.5">
                Notes
              </label>
              <textarea
                rows={6}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Call summary, next steps, objections…"
                data-testid="lead-notes-input"
                className="w-full bg-[#1a1a1a] border border-white/10 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#dc2626]"
              />
            </div>

            <button
              onClick={() => onUpdate({ notes, assigned_to: assigned })}
              data-testid="lead-save-btn"
              className="bg-[#dc2626] hover:bg-[#ef4444] text-white px-5 py-2.5 text-sm font-semibold rounded-md"
            >
              Save changes
            </button>
          </div>

          <div className="text-[11px] font-mono text-neutral-600 border-t border-white/5 pt-4">
            Created {new Date(lead.created_at).toLocaleString()}
            {lead.updated_at && lead.updated_at !== lead.created_at ? (
              <> · Updated {new Date(lead.updated_at).toLocaleString()}</>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

const KV = ({ label, value, link }) => (
  <div>
    <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-1">
      {label}
    </div>
    {link ? (
      <a href={link} className="text-white hover:text-[#dc2626] break-all">
        {value}
      </a>
    ) : (
      <div className="text-white break-all">{value}</div>
    )}
  </div>
);
